import { useMutation, useQuery } from '@apollo/client/react';
import {
  STAFF_SEARCH_ACCOUNTS,
  STAFF_SEARCH_USERS,
  STAFF_SEARCH_DEVICES,
  STAFF_GET_CUSTOMER,
  STAFF_CUSTOMER_DEVICES,
  STAFF_CUSTOMER_FLEETS,
  STAFF_CUSTOMER_TEAMS,
  STAFF_RESOLVE_CUSTOMER,
  STAFF_GET_CUSTOMER_INFO,
  STAFF_GET_CUSTOMER_360,
  STAFF_GET_CUSTOMER_INFO_AUDIT_LOG,
  STAFF_GET_CUSTOMER_NOTES,
  STAFF_ADD_CUSTOMER_NOTE,
  STAFF_GET_ACTIVE_SESSIONS,
  STAFF_UPDATE_CUSTOMER_INFO,
  STAFF_LOGIN_AS_CUSTOMER,
  STAFF_RESTORE_OWN_ACCOUNT,
  STAFF_BILLING_SUBSCRIPTION_STATUS,
} from '@staff-app/graphql-client';
import type {
  AccountSearchResult,
  CustomerInfo,
  CustomerInfoAuditEntry,
  CustomerNote,
  CustomerResolveResult,
  DeviceSearchResult,
  LoginAsCustomerResult,
  StaffCustomer,
  StaffCustomer360,
  StaffDevice,
  StaffLoginSession,
  UserSearchResult,
} from '@staff-app/shared-types';

interface Fleet {
  _id: string;
  name: string;
  description?: string;
  tags?: string[];
  isDefault: boolean;
  deviceCount: number;
  onlineCount: number;
  staleCount: number;
  offlineCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Team {
  _id: string;
  name: string;
  isDefault: boolean;
  isUnassigned: boolean;
  deviceCount: number;
  onlineCount: number;
  staleCount: number;
  offlineCount: number;
  createdAt?: string | null;
}

interface StaffDeviceConnection {
  items: StaffDevice[];
  total: number;
}

/** Skip until the caller has typed at least 2 chars — matches the backend's own floor. */
const MIN_QUERY_LEN = 2;

export function useStaffSearchAccounts(query: string, limit = 25) {
  const trimmed = query.trim();
  return useQuery<{ staffSearchAccounts: AccountSearchResult[] }>(STAFF_SEARCH_ACCOUNTS, {
    variables: { query: trimmed, limit },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffSearchUsers(query: string, limit = 25) {
  const trimmed = query.trim();
  return useQuery<{ staffSearchUsers: UserSearchResult[] }>(STAFF_SEARCH_USERS, {
    variables: { query: trimmed, limit },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffSearchDevices(query: string, limit = 25) {
  const trimmed = query.trim();
  return useQuery<{ staffSearchDevices: DeviceSearchResult[] }>(STAFF_SEARCH_DEVICES, {
    variables: { query: trimmed, limit },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffGetCustomer(accountId: string | undefined) {
  return useQuery<{ staffGetCustomer: StaffCustomer }>(STAFF_GET_CUSTOMER, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffCustomerDevices(accountId: string | undefined) {
  return useQuery<{ staffCustomerDevices: StaffDeviceConnection }>(STAFF_CUSTOMER_DEVICES, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffCustomerFleets(accountId: string | undefined) {
  return useQuery<{ staffCustomerFleets: Fleet[] }>(STAFF_CUSTOMER_FLEETS, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffCustomerTeams(accountId: string | undefined) {
  return useQuery<{ staffCustomerTeams: Team[] }>(STAFF_CUSTOMER_TEAMS, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffResolveCustomer(query: string) {
  const trimmed = query.trim();
  return useQuery<{ staffResolveCustomer: CustomerResolveResult }>(STAFF_RESOLVE_CUSTOMER, {
    variables: { query: trimmed },
    skip: trimmed.length < MIN_QUERY_LEN,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffGetCustomerInfo(accountId: string | null) {
  return useQuery<{ staffGetCustomerInfo: CustomerInfo }>(STAFF_GET_CUSTOMER_INFO, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffGetCustomer360(accountId: string | null) {
  return useQuery<{ staffGetCustomer360: StaffCustomer360 }>(STAFF_GET_CUSTOMER_360, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

/** Subscription health for the customer badge. */
export interface BillingSubscriptionStatus {
  stripeId: string;
  status?: string | null;
  subscriptionId?: string | null;
  multiple: boolean;
}

/**
 * One Stripe read per customer, so it is deliberately cache-first: the badge is decoration on
 * pages the operator opens repeatedly, and re-hitting Stripe on every render of a search result
 * would spend rate limit on a value that changes rarely. `Refresh` on the panel still refetches.
 */
export function useStaffBillingSubscriptionStatus(stripeId: string | null | undefined) {
  return useQuery<{ staffBillingSubscriptionStatus: BillingSubscriptionStatus }>(
    STAFF_BILLING_SUBSCRIPTION_STATUS,
    {
      variables: { stripeId: stripeId ?? '' },
      // No Stripe id → no subscription to ask about, and asking would 400.
      skip: !stripeId,
      fetchPolicy: 'cache-first',
    },
  );
}

export function useStaffGetCustomerInfoAuditLog(accountId: string | null) {
  return useQuery<{ staffGetCustomerInfoAuditLog: CustomerInfoAuditEntry[] }>(
    STAFF_GET_CUSTOMER_INFO_AUDIT_LOG,
    {
      variables: { accountId: accountId ?? '' },
      skip: !accountId,
      fetchPolicy: 'cache-and-network',
    },
  );
}

export function useStaffGetActiveSessions() {
  return useQuery<{ staffGetActiveSessions: StaffLoginSession[] }>(STAFF_GET_ACTIVE_SESSIONS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });
}

export function useStaffUpdateCustomerInfo() {
  return useMutation<
    { staffUpdateCustomerInfo: CustomerInfo },
    { accountId: string; patch: { payment?: string; limitation?: string; feature?: string } }
  >(STAFF_UPDATE_CUSTOMER_INFO);
}

export function useStaffLoginAsCustomer() {
  return useMutation<
    { staffLoginAsCustomer: LoginAsCustomerResult },
    { accountId: string }
  >(STAFF_LOGIN_AS_CUSTOMER, { refetchQueries: [{ query: STAFF_GET_ACTIVE_SESSIONS }] });
}

export function useStaffRestoreOwnAccount() {
  return useMutation<{ staffRestoreOwnAccount: { restored: boolean } }>(
    STAFF_RESTORE_OWN_ACCOUNT,
    { refetchQueries: [{ query: STAFF_GET_ACTIVE_SESSIONS }] },
  );
}

export function useStaffGetCustomerNotes(accountId: string | null) {
  return useQuery<{ staffGetCustomerNotes: CustomerNote[] }>(STAFF_GET_CUSTOMER_NOTES, {
    variables: { accountId: accountId ?? '' },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
}

export function useStaffAddCustomerNote() {
  return useMutation<
    { staffAddCustomerNote: CustomerNote },
    { accountId: string; text: string }
  >(STAFF_ADD_CUSTOMER_NOTE);
}

